import { ComponentProps } from 'react';
import { OddsGridWithQuery as Component } from '@cx-modules/odds-grid-external';
import oddsGridCss from '@cx-modules/odds-grid-external/style.css?inline';
import { widgetBootstrap } from '../../services/cx-module-helper';
import { getFooter } from '../../shared/footer/footer.js';
import {
  Keys,
  SanitisedContractProps as SanitisedProps
} from '../../utils/contractProps/contractProps.js';
import { resolveUsGeo } from '../../utils/geolocation/geolocation.js';
import { isAnalyticsDisabled } from '../../utils/isAnalyticsDisabled/isAnalyticsDisabled.js';
import { html } from '../../utils/lit/html.js';
import disclaimer from './styles/disclaimer';
import itPatches from './styles/it';
import themes from './themes';
import themeContract from './themes/contract';
import { meta } from './meta.js';

export const attributes = meta.attributes;
export const WIDGET_NAME = meta.name;

const {
  VITE_BOOKIE_CLICKOUT_SERVICE_URL: bookieClickoutServiceUrl,
  VITE_CLICKOUT_SERVICE_URL: clickoutServiceUrl,
  VITE_CX_API_HOST: ocApiHost
} = import.meta.env;

const replaceAffId = (s: string, id: string) => {
  return s.replace('{AFF_ID}', id);
};

type BookieInfo = {
  bookmakerName: string;
  position: number;
};

type ContractProps = ComponentProps<typeof Component>;

type OddsFormat = ContractProps['oddsFormat'];
type Repub = ContractProps['repub'];
type CountryCode = ContractProps['countryCode'];
type Subdivision = ContractProps['subdivision'];

type OmittedProps = Keys<
  ContractProps,
  // supplied by widgetBootstrap
  | 'dehydratedState'
  | 'i18n'
  // required upstream, deliberately not passed
  | 'deviceType'
  | 'marketIndex'
  | 'oddsShown'
  | 'resourcesUrl'
  | 'subeventType'
>;

type SanitisedContractProps = SanitisedProps<
  ContractProps,
  OmittedProps,
  'countryCode' | 'subdivision'
>;

const resolveUsGeolocation = (geolocation: string) =>
  resolveUsGeo<CountryCode, Subdivision>(geolocation);

type ContractParams = {
  analytics?: { trackEvent: (event: Record<string, unknown>) => void };
  affIds: Record<string, string>;
  apiKey: string;
  bestOddsStartExpanded: boolean;
  betIdsToShow?: number[];
  bookies: Record<string, BookieInfo>;
  campaignName: string;
  categoryGroupId: number;
  categoryGroupName: string;
  clickoutRedirectTime: number;
  clickoutTemplate: string;
  disableStreamer: boolean;
  enforceMobile: boolean;
  firstBetOnMobileStartsExpanded: boolean;
  geolocation: string;
  gridStartsExpanded: boolean;
  marketId: number;
  marketName: string;
  minUniqueBookies: number;
  noDataAvailableFallbackText: string;
  oddsFormat: OddsFormat;
  recommendedMarketId?: number;
  repub: Repub;
  trackingRate: number;
  showBookieLogo: boolean;
  showMoreBetsStartsExpanded: boolean;
  showSubeventNameAndDatetime: boolean;
  subeventName: string;
};

type ContractAttr = Element & {
  shadowRoot: ShadowRoot | null;
  slotId?: unknown;
  repub?: string;
};

export const contract = async (
  {
    analytics,
    affIds,
    apiKey,
    bestOddsStartExpanded,
    betIdsToShow,
    bookies,
    campaignName,
    categoryGroupId,
    categoryGroupName,
    clickoutRedirectTime,
    clickoutTemplate,
    disableStreamer,
    enforceMobile,
    firstBetOnMobileStartsExpanded,
    geolocation,
    gridStartsExpanded,
    marketId,
    marketName,
    minUniqueBookies,
    noDataAvailableFallbackText,
    oddsFormat,
    recommendedMarketId,
    repub,
    trackingRate,
    showBookieLogo,
    showMoreBetsStartsExpanded,
    showSubeventNameAndDatetime: showSubeventData,
    subeventName
  }: ContractParams,
  attr: ContractAttr
) => {
  const disableAnalytics = isAnalyticsDisabled(trackingRate);
  attr.repub = repub;

  if (repub === 'IT') {
    attr.shadowRoot!.querySelector('style')!.innerHTML += `${itPatches}`;
  }

  if ('IntersectionObserver' in window && !disableAnalytics && analytics) {
    const intersectionObserver = new IntersectionObserver(entries => {
      const entry = entries[0];

      if (entry?.isIntersecting) {
        analytics.trackEvent({
          action: 'impression',
          category: 'Widget',
          event: 'widget_impression',
          label: 'widget_impression',
          page: window.location.href,
          variation: WIDGET_NAME,
          widgetType: 'odds-grid',
          widgetSlot: attr.slotId,
          campaignName,
          ...(recommendedMarketId ? { recommendedMarketId } : {})
        });

        intersectionObserver.disconnect();
      }
    });

    intersectionObserver.observe(attr);
  }

  const onOddsButtonClick: ContractProps['onOddsButtonClick'] = (
    action,
    betId,
    bookieCode,
    betName,
    oddsText
  ) => {
    const event = {
      betName,
      bookieCode,
      categoryGroupName,
      clickLocation: action,
      event: 'widget_clickout',
      marketName,
      odds: oddsText,
      subevent: subeventName,
      variation: WIDGET_NAME
    };

    if (!disableAnalytics && analytics) {
      analytics.trackEvent(event);
    }

    const pathParams = new URLSearchParams();

    pathParams.append('affId', affIds[bookieCode]);
    pathParams.append('analytics', disableAnalytics ? 'off' : 'on');
    pathParams.append('bookieCode', bookieCode);
    pathParams.append('redirectInterval', String(clickoutRedirectTime));
    pathParams.append('template', String(clickoutTemplate));

    pathParams.append('apiKey', String(apiKey));
    pathParams.append('betIds', String(betId));

    if (recommendedMarketId) {
      pathParams.append('recommendedMarketId', String(recommendedMarketId));
    }

    const redirectUrl = `${clickoutServiceUrl}?${pathParams.toString()}`;

    window.open(redirectUrl, '_blank', 'noopener noreferrer');
  };

  const onOfferClick: ContractProps['onOfferClick'] = (
    action,
    bookieCode,
    clickEvent,
    clickoutUrl
  ) => {
    clickEvent.preventDefault(); // Stop the redirect so we can use our clickout service.

    if (repub === 'IT') return;

    const pageUrl = window.location.href;

    const bookieLogoContainerDesktop = attr.shadowRoot!.querySelector(
      '[class^=bookmakersRowWrapper_]'
    );
    const bookieLogoContainerMobile = attr.shadowRoot!.querySelector(
      '[class^=oddsAreaWrapper_]'
    );

    const bookieLogoContainer = (bookieLogoContainerDesktop ||
      bookieLogoContainerMobile)!.querySelectorAll<HTMLElement>('[data-bk]');

    const offerIndex = [...bookieLogoContainer].findIndex(
      logoElement => logoElement.dataset.bk === bookieCode
    );

    const event = {
      action,
      bookieCode,
      category: action,
      event: 'offer_clickouts',
      offerIndex,
      offerName: bookies[bookieCode].bookmakerName,
      page: pageUrl,
      repub,
      variation: WIDGET_NAME
    };

    if (!disableAnalytics && analytics) {
      analytics.trackEvent(event);
    }

    const pathParams = new URLSearchParams();

    pathParams.append('affId', affIds[bookieCode]);
    pathParams.append('analytics', disableAnalytics ? 'off' : 'on');
    pathParams.append('bk', bookieCode);
    pathParams.append('redirectInterval', String(clickoutRedirectTime));
    pathParams.append('template', String(clickoutTemplate));

    pathParams.append(
      'burl',
      decodeURIComponent(replaceAffId(clickoutUrl, affIds[bookieCode]))
    );
    pathParams.append('page_url', pageUrl);
    pathParams.append('repub', String(repub));

    const redirectUrl = `${bookieClickoutServiceUrl}?${pathParams.toString()}`;

    window.open(redirectUrl, '_blank', 'noopener noreferrer');
  };

  const isUsRepub = repub === 'US';

  // sdk-context-engine assigns `position`, which is the ordering of the bookmaker as set in connect
  // the odds-grid module uses `priority` which is for oddschecker estates, so we map our position to the `priority` key
  // ensuring the correct ordering on the rendered grid.
  const overrideBookies = Object.entries(bookies).map(
    ([code, { position }]) => ({
      code,
      priority: position
    })
  );

  const cxProps: SanitisedContractProps = {
    apiKey,
    bestOddsStartExpanded,
    betIdsToShow,
    categoryGroupId,
    dataApiEndpoint: ocApiHost,
    disableStreamer,
    enforceMobile,
    firstBetOnMobileStartsExpanded,
    gridStartsExpanded,
    hideHistory: true,
    marketId,
    marketName,
    minUniqueBookies,
    noDataAvailableFallbackText,
    oddsFormat,
    onOddsButtonClick,
    onOfferClick,
    overrideBookies,
    repub,
    showBookieLogo,
    showMoreBetsStartsExpanded,
    showSubeventData,
    ...(isUsRepub && resolveUsGeolocation(geolocation))
  };

  return cxProps;
};

type DisclaimerServiceParams = {
  aiContextual?: unknown;
  marketId?: unknown;
  widgetStyle?: string;
  repub?: string;
};

const disclaimerService = (
  { aiContextual, marketId, widgetStyle, repub }: DisclaimerServiceParams,
  { shadowRoot }: { shadowRoot: ShadowRoot | null }
) => {
  const shouldInjectCxModule = (aiContextual && marketId) || !aiContextual;
  const disclaimerSlot = shadowRoot?.querySelector<HTMLSlotElement>(
    'slot[name="disclaimer"]'
  );

  if (repub === 'IT' && shouldInjectCxModule) {
    disclaimerSlot!.innerHTML = html`${getFooter(widgetStyle as string)}`;
    disclaimerSlot!.hidden = false;
  }
};

type QueryKeyParams = {
  marketId?: unknown;
  repub?: string;
  geolocation: string;
  bookies: Record<string, unknown>;
};

export const queryKey = ({
  marketId,
  repub,
  geolocation,
  bookies
}: QueryKeyParams) => {
  const overrideBookies = Object.keys(bookies);
  const isUsRepub = repub === 'US';
  const usGeo = isUsRepub ? resolveUsGeolocation(geolocation) : undefined;

  return [
    'odds-grid',
    repub,
    usGeo?.countryCode,
    usGeo?.subdivision,
    marketId,
    overrideBookies?.sort().join(',') ?? null
  ];
};

widgetBootstrap({
  attributes,
  Component,
  contract,
  customCss: disclaimer,
  disclaimerService,
  disclaimer: (x: unknown) => x,
  name: WIDGET_NAME,
  style: oddsGridCss,
  themes,
  themeContract,
  version: '8.12.0',
  queryKey
});
